import React from 'react';
import { formatReportDate } from '../../../logic/cmsWindows';

function fmtShort(at) {
  const d = new Date(at);
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts); // locale pinned — mono-language English UI
}

function ms(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

/* Care period milestone progress bar:
 * Enrolled → Labs → Visit → Aligned → Shipped → Baseline → PCP → Q1 → Q2 → Q3 → Final.
 * Baseline sits BEFORE PCP (2026-08-27): the baseline submission SLA is alignment+3 and the CMS
 * Care-Initiation update to the PCP is alignment+10 — and the RFA (p36) requires that update to
 * communicate "baseline measures", so the baseline has to exist first. It read PCP → Baseline while
 * the submission SLA was +21, which was later than the communication depending on it.
 * Lifecycle points come off the care-period row; Labs/Visit/Shipped/PCP off the patient's completed
 * onb_baseline_labs_review / onb_virtual_visit / onb_device_ship / onb_care_plan_share tasks
 * (taskMilestoneDates, epoch ms); reporting points off milestoneDates (logic/cmsWindows
 * milestoneReportDates).
 * Done = ✓-filled dot + date under the label; remaining = hollow dot.
 * Fill line = leading consecutive-done prefix, NOT calendar time — it stalls at the first
 * pending milestone even when later dots are done (the gap is the coordinator's cue).
 * `at` convention everywhere: number = done on that date; undefined = done, date
 * unknown; null = remaining. */
export default function MilestoneBar({ carePeriod, milestoneDates, taskMilestoneDates }) {
  if (!carePeriod) return null;
  const reportingDone = (slot) => {
    const at = milestoneDates?.[slot] ?? null;
    if (at != null) return at;
    const st = carePeriod.milestones?.[slot]?.status;
    return (st === 'completed' || st === 'submitted') ? undefined : null;
  };
  // Key-presence read, not `?? null`: undefined means "done, date unknown" and must survive.
  const taskAt = (k) => (taskMilestoneDates && k in taskMilestoneDates ? taskMilestoneDates[k] : null);
  const points = [
    { key: 'enrolled', label: 'Enrolled', at: ms(carePeriod.start_date), tip: 'Enrolled' },
    { key: 'labs', label: 'Labs', at: taskAt('labs'), tip: 'Baseline labs reviewed' },
    { key: 'visit', label: 'Visit', at: taskAt('visit'), tip: 'Initial Virtual Visit completed' },
    { key: 'aligned', label: 'Aligned', at: ms(carePeriod.cms_alignment_date), tip: 'CMS aligned' },
    { key: 'ship', label: 'Shipped', at: taskAt('ship'), tip: 'Devices shipped' },
    // Baseline (alignment+3) precedes the PCP update (alignment+10) — see the header note.
    { key: 'baseline', label: 'Baseline', at: reportingDone('baseline'), tip: 'Baseline report submitted to CMS' },
    { key: 'pcp', label: 'PCP', at: taskAt('pcp'), tip: 'Care plan shared with PCP' },
    ...[['q1', 'Q1'], ['q2', 'Q2'], ['q3', 'Q3'], ['final', 'Final']]
      .map(([slot, label]) => ({ key: slot, label, at: reportingDone(slot), tip: `${label} report submitted to CMS` })),
  ];
  const firstPending = points.findIndex((p) => p.at === null);
  const fillIndex = firstPending === -1 ? points.length - 1 : firstPending - 1;
  const progress = Math.max(0, (fillIndex / (points.length - 1)) * 100);

  return (
    <div style={{ margin: '16px 0 0' }}>
      <div style={{ position: 'relative', height: 20 }}>
        <div style={{ position: 'absolute', top: 9, left: 0, right: 0, height: 3, background: 'var(--wi-divider)', borderRadius: 2 }} />
        <div
          role="progressbar"
          aria-label="Care period milestone progress"
          aria-valuenow={Math.round(progress)}
          style={{ position: 'absolute', top: 9, left: 0, width: `${progress}%`, height: 3, background: 'var(--wi-action-primary)', borderRadius: 2, transition: 'width 300ms ease' }}
        />
        {points.map((p, i) => {
          const pct = (i / (points.length - 1)) * 100;
          const done = p.at !== null;
          return (
            <div key={p.key} style={{
              position: 'absolute', left: `${pct}%`, top: '50%',
              transform: 'translate(-50%, -50%)',
              width: done ? 14 : 10, height: done ? 14 : 10, borderRadius: '50%',
              background: done ? 'var(--wi-action-primary)' : 'var(--wi-background-secondary)',
              border: done ? 'none' : '2px solid var(--wi-divider)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            >
              {done && <span aria-hidden="true" style={{ fontSize: 8, lineHeight: 1, color: 'var(--wi-on-action-primary)' }}>✓</span>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        {points.map((p) => {
          const done = p.at !== null;
          const full = p.at != null ? `${p.tip} ${formatReportDate(p.at)}` : (done ? p.tip : undefined);
          return (
            <span
              key={p.key}
              title={full}
              aria-label={full}
              style={{ fontSize: 11, color: 'var(--wi-text-secondary)', textAlign: 'center', flex: 1, whiteSpace: 'nowrap' }}
            >
              {p.label}
              {p.at != null && (
                <span style={{ display: 'block', fontSize: 10, color: 'var(--wi-action-primary)', fontWeight: 500 }}>
                  ✓ {fmtShort(p.at)}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
